import React, { useState } from 'react';
import {
  Paper,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import { MapProvider as MapProviderType } from './MapInterface';
import { useMapProvider } from './MapProvider';

interface AdminMapSettingsProps {
  className?: string;
}

const AdminMapSettings: React.FC<AdminMapSettingsProps> = ({ className }) => {
  const { provider, setProvider, isLoaded, error } = useMapProvider();
  const [isChanging, setIsChanging] = useState(false);

  const handleProviderChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newProvider = event.target.value as MapProviderType;
    
    if (newProvider === provider) return;

    setIsChanging(true);
    try {
      await setProvider(newProvider);
    } finally {
      setIsChanging(false);
    }
  };

  if (!isLoaded) {
    return (
      <Paper sx={{ p: 2, mb: 2 }} className={className}>
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography>Loading map provider settings...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 2 }} className={className}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Map Provider Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose the map provider for all users on the platform. Changes take effect immediately.
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormControl component="fieldset" disabled={isChanging}>
        <RadioGroup
          value={provider}
          onChange={handleProviderChange}
          name="map-provider"
        >
          <FormControlLabel
            value="google"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Google Maps</Typography>
                {provider === 'google' && (
                  <Chip label="Current" size="small" color="primary" />
                )}
              </Box>
            }
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 2, display: 'block' }}>
            Full-featured mapping with Places API, Street View, and comprehensive location data
          </Typography>

          <FormControlLabel
            value="mapbox"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Mapbox</Typography>
                {provider === 'mapbox' && (
                  <Chip label="Current" size="small" color="primary" />
                )}
              </Box>
            }
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 2, display: 'block' }}>
            High-performance mapping with custom styling and competitive pricing
          </Typography>
        </RadioGroup>
      </FormControl>

      {isChanging && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Updating map provider...
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" color="text.secondary">
        <strong>Note:</strong> This setting affects all users immediately. Map data and features may vary between providers.
        Ensure your API keys are properly configured for both providers.
      </Typography>
    </Paper>
  );
};

export default AdminMapSettings;